import { useEffect, useRef, type ReactNode } from "react";

interface DrawerProps {
	isOpen: boolean;
	onClose: () => void;
	children: ReactNode;
	/** Which side the drawer slides from */
	side?: "left" | "right";
}

/**
 * Slide-out drawer overlay for mobile navigation.
 * Renders a backdrop + sliding panel. Supports backdrop click and
 * horizontal swipe-to-close gestures.
 */
export function Drawer({ isOpen, onClose, children, side = "left" }: DrawerProps) {
	const panelRef = useRef<HTMLDivElement>(null);
	const touchStartX = useRef<number | null>(null);

	// Lock body scroll when open
	useEffect(() => {
		if (isOpen) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "";
		}
		return () => {
			document.body.style.overflow = "";
		};
	}, [isOpen]);

	// Close on Escape key
	useEffect(() => {
		if (!isOpen) return;
		function handleKey(e: KeyboardEvent) {
			if (e.key === "Escape") onClose();
		}
		window.addEventListener("keydown", handleKey);
		return () => window.removeEventListener("keydown", handleKey);
	}, [isOpen, onClose]);

	function handleTouchStart(e: React.TouchEvent) {
		const touch = e.touches[0];
		if (touch) touchStartX.current = touch.clientX;
	}

	function handleTouchEnd(e: React.TouchEvent) {
		if (touchStartX.current === null) return;
		const touch = e.changedTouches[0];
		if (!touch) return;
		const dx = touch.clientX - touchStartX.current;
		// Swipe left to close a left-side drawer, swipe right for right-side
		const threshold = 80;
		if (side === "left" && dx < -threshold) onClose();
		if (side === "right" && dx > threshold) onClose();
		touchStartX.current = null;
	}

	const translateFrom = side === "left" ? "-translate-x-full" : "translate-x-full";
	const panelPosition = side === "left" ? "left-0" : "right-0";

	return (
		<div
			className={`fixed inset-0 z-40 transition-opacity duration-300 ${
				isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
			}`}
		>
			{/* Backdrop */}
			<div
				className="absolute inset-0 bg-black/60"
				onClick={onClose}
			/>

			{/* Sliding panel */}
			<div
				ref={panelRef}
				className={`absolute top-0 ${panelPosition} h-full w-[320px] max-w-[85vw] bg-bg-secondary border-r border-border-default shadow-lg transform transition-transform duration-300 ease-out ${
					isOpen ? "translate-x-0" : translateFrom
				}`}
				onTouchStart={handleTouchStart}
				onTouchEnd={handleTouchEnd}
			>
				{children}
			</div>
		</div>
	);
}
